import { Lock } from 'lucide-react'
import { Link } from 'react-router'
import { showDate } from '../../shared/tree'
import { Avatar } from '@/components/tree/Avatar'
import { photoSrcSet } from '@/lib/albums'
import {
  audience,
  isPrivate,
  momentPhotoUrl,
  paragraphs,
  written,
  type MomentPhoto,
  type MomentSummary,
} from '@/lib/moments'
import { tint } from '@/lib/paper'
import { useIsPhone } from '@/lib/platform'
import { cn } from '@/lib/utils'

const link = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'

// Who wrote it and when, and who sees it: the line above a moment's title.
export function Byline({
  moment,
  whole,
}: {
  moment: Pick<MomentSummary, 'author' | 'createdAt' | 'sharedAt' | 'space' | 'sharedWith' | 'everyone'>
  // Say every place it's shared, not just who sees it in this circle (on its own page).
  whole?: boolean
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm">
      <Avatar person={moment.author} className="size-9 text-sm shadow-none" />
      <span className="font-medium">{moment.author.name}</span>
      <span className="text-muted-foreground">{written(moment.sharedAt ?? moment.createdAt)}</span>
      <span
        className={cn(
          'ml-auto flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
          isPrivate(moment) ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground',
        )}
      >
        {isPrivate(moment) && <Lock className="size-3" aria-hidden />}
        <span className="sr-only">Who sees it: </span>
        {audience(moment, whole)}
      </span>
    </div>
  )
}

// On a tablet or computer the feed shows about half of what a list sends of each story, so more stories fit.
const SHORT = 300
const shorter = (body: string) => (body.length > SHORT ? `${body.slice(0, SHORT).replace(/\s+\S*$/, '')}…` : body)

// One moment in the feed: who wrote it, its title, the start of the story and its first photos, opening to the whole.
// On a phone the photos span the screen under the title. On a tablet or computer the title and about half as much of
// the story come first, with the first photo a quarter of the width at the right, so more of the feed fits (photos
// are full size on the moment's own page).
export function MomentCard({ moment, headingLevel = 2 }: { moment: MomentSummary; headingLevel?: 2 | 3 }) {
  const Heading = `h${headingLevel}` as const
  const to = `/moment/${moment.id}`
  const id = `moment-${moment.id}`
  const phone = useIsPhone()
  const cover = moment.photos[0]
  const body = phone ? moment.body : shorter(moment.body)
  const more = moment.more || body !== moment.body
  const title = (
    <div className="flex flex-col gap-1">
      {moment.happenedOn && <p className="eyebrow">{showDate(moment.happenedOn)}</p>}
      <Heading id={id} className="text-title font-semibold tracking-tight text-balance">
        <Link to={to} className={cn('hover:underline hover:underline-offset-4', link)}>
          {moment.title}
        </Link>
      </Heading>
    </div>
  )
  const story = (
    <>
      {body && (
        <div className="flex flex-col gap-3 text-body text-foreground/90">
          {paragraphs(body).map((p, i) => (
            <p key={i} className="whitespace-pre-line">
              {p}
            </p>
          ))}
        </div>
      )}
      {more && (
        <Link to={to} className={cn('self-start text-sm font-medium text-brand underline underline-offset-4', link)}>
          Read the rest<span className="sr-only"> of {moment.title}</span>
        </Link>
      )}
    </>
  )
  return (
    <article aria-labelledby={id} className="flex flex-col gap-4 border-b pb-10 last:border-b-0 nav:pb-8">
      <Byline moment={moment} />
      {phone ? (
        <>
          {title}
          {cover && <Photos to={to} title={moment.title} photos={moment.photos} n={moment.photoCount} />}
          {story}
        </>
      ) : (
        <div className="flex items-start gap-6">
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            {title}
            {story}
          </div>
          {cover && <Cover to={to} title={moment.title} photo={cover} n={moment.photoCount} />}
        </div>
      )}
    </article>
  )
}

// A moment's first photos: one shows whole and wide; two side by side; three or more as one large and two small,
// with how many more there are on the last.
function Photos({ to, title, photos, n }: { to: string; title: string; photos: MomentPhoto[]; n: number }) {
  const shown = photos.slice(0, 3)
  const extra = n - shown.length
  const one = shown.length === 1
  return (
    <Link
      to={to}
      aria-label={`${n === 1 ? 'The photo' : `The ${n} photos`} in ${title}`}
      className={cn(
        'grid gap-1 overflow-hidden rounded-2xl',
        shown.length === 2 && 'grid-cols-2',
        shown.length >= 3 && 'aspect-[3/2] grid-cols-3 grid-rows-2',
        link,
      )}
    >
      {shown.map((photo, i) => (
        <span
          key={photo.id}
          className={cn(
            'relative block overflow-hidden',
            tint(i),
            shown.length === 2 && 'aspect-[4/3]',
            shown.length >= 3 && (i === 0 ? 'col-span-2 row-span-2' : ''),
          )}
          style={one ? { aspectRatio: String(Math.min(Math.max(photo.width / photo.height, 1.25), 2)) } : undefined}
        >
          <img
            src={momentPhotoUrl(photo.id, 'medium')}
            srcSet={photoSrcSet({ ...photo, ref: photo.id }, ['thumb', 'medium', 'display'], momentPhotoUrl)}
            sizes={one || i === 0 ? '(min-width: 768px) 44rem, 100vw' : '(min-width: 768px) 15rem, 33vw'}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover"
          />
          {i === shown.length - 1 && extra > 0 && (
            <span className="absolute inset-0 grid place-items-center bg-viewer/55 text-lg font-semibold text-viewer-foreground">
              +{extra}
            </span>
          )}
        </span>
      ))}
    </Link>
  )
}

// A moment's first photo, small and square to the right of its story, with how many more there are.
function Cover({ to, title, photo, n }: { to: string; title: string; photo: MomentPhoto; n: number }) {
  return (
    <Link
      to={to}
      aria-label={`${n === 1 ? 'The photo' : `The ${n} photos`} in ${title}`}
      className={cn('relative block aspect-square w-1/4 shrink-0 overflow-hidden rounded-2xl', tint(0), link)}
    >
      <img
        src={momentPhotoUrl(photo.id, 'thumb')}
        srcSet={photoSrcSet({ ...photo, ref: photo.id }, ['thumb', 'medium'], momentPhotoUrl)}
        sizes="(min-width: 768px) 12rem, 25vw"
        alt=""
        loading="lazy"
        decoding="async"
        className="size-full object-cover"
      />
      {n > 1 && (
        <span className="absolute right-1.5 bottom-1.5 rounded-full bg-viewer/60 px-2 py-0.5 text-xs font-medium text-viewer-foreground">
          +{n - 1}
        </span>
      )}
    </Link>
  )
}

// The opening of a story, for the card and list views: its first paragraph, which they clamp to fit.
const opening = (body: string) => paragraphs(body)[0] ?? ''

// A link that covers its whole card or row, so anywhere on it opens the moment and screen readers hear one name.
const stretched = (round: string) =>
  cn('outline-none after:absolute after:inset-0 focus-visible:after:ring-[3px] focus-visible:after:ring-ring/50', round)

// Kept just for its author, said as a small lock.
function OnlyYou({ moment }: { moment: MomentSummary }) {
  if (!isPrivate(moment)) return null
  return (
    <span className="flex shrink-0 items-center text-muted-foreground" title="Only you">
      <Lock className="size-3.5" aria-hidden />
      <span className="sr-only">Who sees it: Only you</span>
    </span>
  )
}

// One moment as a card in a grid: its first photo (or, with none, the opening of the story on paper), its title and
// who wrote it. Many fit on a screen; the whole opens on its own page.
export function MomentTile({ moment, index }: { moment: MomentSummary; index: number }) {
  const id = `moment-${moment.id}`
  const cover = moment.photos[0]
  const text = opening(moment.body)
  return (
    <article
      aria-labelledby={id}
      className="group relative flex flex-col overflow-hidden rounded-[1.25rem] border transition-[translate,box-shadow] duration-300 ease-settle hover:translate-y-[-2px] hover:shadow-lift"
    >
      <div className={cn('relative aspect-[4/3] overflow-hidden', tint(index))}>
        {cover ? (
          <img
            src={momentPhotoUrl(cover.id, 'thumb')}
            srcSet={photoSrcSet({ ...cover, ref: cover.id }, ['thumb', 'medium'], momentPhotoUrl)}
            sizes="(min-width: 640px) 15rem, 50vw"
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover transition-[scale] duration-300 ease-settle group-hover:scale-[1.02]"
          />
        ) : (
          <p className="line-clamp-5 p-4 text-sm text-foreground/80 sm:line-clamp-6">{text}</p>
        )}
        {moment.photoCount > 1 && (
          <span className="absolute right-2 bottom-2 rounded-full bg-viewer/60 px-2 py-0.5 text-xs font-medium text-viewer-foreground">
            {moment.photoCount}
            <span className="sr-only"> photos</span>
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3 sm:p-4">
        {moment.happenedOn && <p className="eyebrow text-[0.625rem]">{showDate(moment.happenedOn)}</p>}
        <h2 id={id} className="line-clamp-2 leading-tight font-semibold tracking-tight text-balance">
          <Link to={`/moment/${moment.id}`} className={stretched('after:rounded-[1.25rem]')}>
            {moment.title}
          </Link>
        </h2>
        {cover && text && <p className="line-clamp-2 text-sm text-muted-foreground max-sm:hidden">{text}</p>}
        <div className="mt-auto flex items-center gap-1.5 pt-1 text-xs text-muted-foreground">
          <Avatar person={moment.author} className="size-5 text-[0.625rem] shadow-none" />
          <span className="min-w-0 truncate">{moment.author.name}</span>
          <span className="ml-auto flex shrink-0 items-center gap-1.5">
            {written(moment.sharedAt ?? moment.createdAt)}
            <OnlyYou moment={moment} />
          </span>
        </div>
      </div>
    </article>
  )
}

// One moment as a row in a list: a small first photo, its title, who wrote it and when, and the opening line. The
// most moments at a glance.
export function MomentRow({ moment, index }: { moment: MomentSummary; index: number }) {
  const id = `moment-${moment.id}`
  const cover = moment.photos[0]
  const text = opening(moment.body)
  return (
    <article
      aria-labelledby={id}
      className="relative flex items-center gap-3 rounded-2xl px-2 py-2.5 transition-colors hover:bg-secondary/60 sm:gap-4"
    >
      <span className={cn('grid size-14 shrink-0 place-items-center overflow-hidden rounded-xl', tint(index))}>
        {cover ? (
          <img
            src={momentPhotoUrl(cover.id, 'thumb')}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover"
          />
        ) : (
          <Avatar person={moment.author} className="size-9 text-sm shadow-none" />
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <h2 id={id} className="truncate font-semibold tracking-tight">
          <Link to={`/moment/${moment.id}`} className={stretched('after:rounded-2xl')}>
            {moment.title}
          </Link>
        </h2>
        <p className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
          <span className="truncate">
            {[
              moment.author.name,
              written(moment.sharedAt ?? moment.createdAt),
              moment.happenedOn && showDate(moment.happenedOn),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          <OnlyYou moment={moment} />
        </p>
        {text && <p className="truncate text-sm text-foreground/80 max-sm:hidden">{text}</p>}
      </div>
    </article>
  )
}
